/**
 * cardDisplay — how a card's rarity and price are *labelled*.
 *
 * Kept out of Card.jsx so the grid tile and the detail sheet cannot drift apart,
 * and so the labelling rules are unit-testable without rendering.
 */
import { getConditionPrice } from "../utils/priceAdapter";
import { DEFAULT_CONDITION } from "../utils/conditionScale";
import { formatPrice } from "../utils/currency";

// ── Rarity ───────────────────────────────────────────────────────────────────

/**
 * Rarity styles, MOST SPECIFIC FIRST.
 *
 * Two things make this trickier than a substring scan:
 *
 * 1. Order is load-bearing. A scan that reached "rare" early collapsed every
 *    Scarlet & Violet-era rarity ("Special Illustration Rare", "Double Rare",
 *    "ACE SPEC Rare", …) into a generic teal "Rare" badge — a lie on exactly
 *    the chase cards people hunt.
 * 2. Word order is not stable across eras. The API says "Rare Secret" and
 *    "Rare Holo" for older sets but "Special Illustration Rare" for newer ones,
 *    so rules match on an unordered set of tokens, not a contiguous phrase.
 *
 * `exact: true` means the card's whole token set must equal the rule's — it
 * keeps the generic tiers from swallowing qualified rarities we don't know yet
 * ("Rare BREAK" falls through to the raw string rather than becoming "Rare").
 */
const RARITY_RULES = [
  // Scarlet & Violet era
  { tokens: ["special", "illustration"], label: "Special Illustration Rare", color: "#be185d", glow: true },
  { tokens: ["illustration"],            label: "Illustration Rare",         color: "#c2410c", glow: true },
  { tokens: ["mega", "hyper"],           label: "Mega Hyper Rare",           color: "#7c3aed", glow: true },
  { tokens: ["hyper"],                   label: "Hyper Rare",                color: "#a21caf", glow: true },
  { tokens: ["ace", "spec"],             label: "ACE SPEC Rare",             color: "#b91c1c", glow: true },
  { tokens: ["shiny", "ultra"],          label: "Shiny Ultra Rare",          color: "#0891b2", glow: true },
  { tokens: ["double"],                  label: "Double Rare",               color: "#1d4ed8", glow: true },
  { tokens: ["triple"],                  label: "Triple Rare",               color: "#1e40af", glow: true },
  // Long-standing premium tiers
  { tokens: ["secret"],                  label: "Secret Rare",               color: "#7c3aed", glow: true },
  { tokens: ["rainbow"],                 label: "Rainbow Rare",              color: "#db2777", glow: true },
  { tokens: ["amazing"],                 label: "Amazing Rare",              color: "#0d9488", glow: true },
  { tokens: ["radiant"],                 label: "Radiant Rare",              color: "#ca8a04", glow: true },
  { tokens: ["shiny"],                   label: "Shiny Rare",                color: "#0891b2", glow: true },
  { tokens: ["full", "art"],             label: "Full Art",                  color: "#0369a1", glow: true },
  { tokens: ["ultra"],                   label: "Ultra Rare",                color: "#6b21a8", glow: true },
  { tokens: ["legendary"],               label: "Legendary",                 color: "#d97706", glow: true },
  { tokens: ["holo"],                    label: "Holo Rare",                 color: "#6b21a8", glow: true },
  // Generic tiers — exact matches only, so anything qualified falls through.
  { tokens: ["promo"],    label: "Promo",    color: "#0f766e", glow: false, exact: true },
  { tokens: ["rare"],     label: "Rare",     color: "#0f766e", glow: false, exact: true },
  { tokens: ["uncommon"], label: "Uncommon", color: "#475569", glow: false, exact: true },
  { tokens: ["common"],   label: "Common",   color: "#64748b", glow: false, exact: true },
];

const tokenize = (s) => s.toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);

/** Title-cases an unrecognised rarity so it can be shown verbatim. */
const titleCase = (s) =>
  s.replace(/\w[^\s-]*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

/**
 * Badge descriptor for a card's rarity, or null when the card has none.
 *
 * An unrecognised rarity falls through to the raw API string rather than a
 * wrong-but-familiar label — a new set's chase rarity should read as itself,
 * not as "Rare".
 */
export const getRarityBadge = (card) => {
  const raw = (card?.rarity || "").trim();
  if (!raw) return null;

  const tokens = tokenize(raw);
  const has = (t) => tokens.includes(t);

  const rule = RARITY_RULES.find((r) =>
    r.exact
      ? tokens.length === r.tokens.length && r.tokens.every(has)
      : r.tokens.every(has)
  );
  if (rule) {
    return { label: rule.label, color: rule.color, glow: rule.glow };
  }

  // Unknown rarity — show what the API said.
  return { label: titleCase(raw), color: "#475569", glow: false };
};

/**
 * Whether a card should be rendered with the foil treatment.
 *
 * Reuses the badge's `glow` flag rather than keeping a second list: the tiers
 * worth glowing and the tiers printed on foil stock are the same set, and two
 * lists would drift. An unrecognised rarity does not shimmer — claiming a card
 * is a chase card because we failed to parse its rarity is the wrong way to be
 * wrong.
 */
export const isFoilRarity = (card) => getRarityBadge(card)?.glow === true;

// ── Price ────────────────────────────────────────────────────────────────────

export const NO_PRICE_LABEL = "No estimate";

/**
 * How a card's price should be presented.
 *
 * Every number the grid shows today is a multiplier estimate off a market
 * average, so it must carry "Est." framing. Only a verified per-condition sales
 * feed (card.conditionPrices) earns an unqualified figure.
 *
 * The amount string always comes from the currency boundary — callers with a
 * currency preference in scope pass `useCurrency().formatAmount`; without one
 * the figure's own currency is rendered, never a wrong symbol.
 *
 * The figure's currency is threaded through to the formatter rather than left
 * implicit: a Cardmarket estimate is EUR and a provider quote is USD, and the
 * tile has to print the symbol belonging to the number it is showing. `source`
 * and `currency` come back out so the detail sheet can attribute the figure to
 * the right provider (see currency.js priceProvenance).
 *
 * @param {(amount: number, currency: string|null) => string} [formatAmount]
 * @returns {{ hasPrice: boolean, amount: string|null, label: string,
 *             isEstimate: boolean, qualifier: string|null,
 *             currency: string|null, source: string|null }}
 */
export const getPriceDisplay = (
  card,
  condition = DEFAULT_CONDITION,
  formatAmount = (n, c) => formatPrice(n, { amountCurrency: c }).text
) => {
  const { price, currency, isEstimate, source } = getConditionPrice(card, condition);

  if (!(price > 0)) {
    return {
      hasPrice: false,
      amount: null,
      label: NO_PRICE_LABEL,
      isEstimate: true,
      qualifier: null,
      currency: null,
      source: null,
    };
  }

  const amount = formatAmount(price, currency);
  return {
    hasPrice: true,
    amount,
    label: isEstimate ? `Est. ${amount}` : amount,
    isEstimate,
    qualifier: isEstimate ? "Est." : "Market",
    currency,
    source,
  };
};
